import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Bar, Pie } from "react-chartjs-2";
import {
  Chart,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import "../../styles/Dashboard.css";
import { Button, Form, Popup, Grid } from "semantic-ui-react";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { getChatHistoryData } from "../../apis/index";
import {
  fetchEngagement,
  fetchEngagementSuccess,
} from "../../actions/dataConsoleAction"; // Adjust the import path as needed
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DashLoader from "../../components/DashLoader";
import DropLoader from "../../components/DropLoader";
import { minHeight } from "@mui/system";
import DownloadIcon from "../../components/DownloadIcon";
import { handleExport } from "../../utils/exportUtils";

// Register the plugins for Chart.js
Chart.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  ChartDataLabels
);

const ConversationEngagement = ({
  selectedDatasetId,
  selectedDatasetName,
  globalStartDate,
  globalEndDate,
  globalSelectedGroup,
  globalSelectedUsers, // Array of selected user IDs
  globalUsers,
  globalEngagementData,
  globalEngagementLoading,
  globalGroups, // Groups list from parent
}) => {
  const [chatTime, setChatTime] = useState(0);
  const datasetId = selectedDatasetId.selectedDatasetId || "";

  // Helper function to get selected users display text
  const getSelectedUsersText = () => {
    if (!globalSelectedUsers || globalSelectedUsers.length === 0) return 'None';
    if (globalSelectedUsers.length === globalUsers.length) return 'All Users';
    if (globalSelectedUsers.length === 1) {
      const user = globalUsers.find(u => u.userId === globalSelectedUsers[0]);
      return user ? user.username : globalSelectedUsers[0];
    }
    return `${globalSelectedUsers.length} Users`;
  };

  // Helper function to get group name from ID
  const getGroupName = () => {
    if (!globalSelectedGroup) return 'All Groups';
    const group = globalGroups.find(g => g.id === globalSelectedGroup);
    return group ? group.name : 'All Groups';
  };

  const convertMinutesToHours = (timeString) => {
    const timeParts = timeString.split(" ");
    let totalSeconds = 0;

    for (let i = 0; i < timeParts.length; i += 2) {
      const value = parseInt(timeParts[i], 10);
      const unit = timeParts[i + 1];

      if (unit.startsWith("hr")) {
        totalSeconds += value * 3600;
      } else if (unit.startsWith("min")) {
        totalSeconds += value * 60;
      } else if (unit.startsWith("sec")) {
        totalSeconds += value;
      }
    }

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const formattedTime = [
      hours > 0 ? `${hours} hr` : "",
      minutes > 0 ? `${minutes} min` : "",
      seconds > 0 ? `${seconds} sec` : "",
    ]
      .filter(Boolean)
      .join(" ");

    return formattedTime;
  };

  // Parse "YYYY-MM-DD" as local date (not UTC)
  const parseLocalDate = (dateStr) => {
    if (!dateStr) return new Date(NaN);
    const [year, month, day] = dateStr.split("-").map(Number);
    return new Date(year, month - 1, day);
  };


  const formatDate = (date) => {
    if (!date) return "";
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Utility function to generate date range array
  const generateDateRange = (start, end) => {
    if (!start || !end) return [];
    const dateArray = [];
    let currentDate = new Date(start);
    while (currentDate <= end) {
      dateArray.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return dateArray;
  };

  const findMatchingWeekData = (dataArray, startDate, endDate) => {
    if (!dataArray || dataArray.length === 0) return null;

    const start = new Date(startDate);
    const end = new Date(endDate);

    // Find the week that contains the selected date range
    return dataArray.find((item) => {
      if (item.week) {
        // Parse the week string "2025-09-22 - 2025-09-28"
        const weekParts = item.week.split(" - ");
        if (weekParts.length === 2) {
          const weekStart = parseLocalDate(weekParts[0]);
          const weekEnd = parseLocalDate(weekParts[1]);

          // Check if the selected range overlaps with this week
          return (
            (start >= weekStart && start <= weekEnd) ||
            (end >= weekStart && end <= weekEnd) ||
            (start <= weekStart && end >= weekEnd)
          );
        }
      }
      if (item.date) {
        // For date-based data, check if it falls within range
        const itemDate = parseLocalDate(item.date);
        return itemDate >= start && itemDate <= end;
      }
      return false;
    });
  };

  // Average Conversation Length
  const renderAverageConversationLength = () => {
    const chartRef = useRef(null);
    const dateRange = generateDateRange(globalStartDate, globalEndDate);
    const [weeklyData, setWeeklyData] = useState(
      Array(dateRange.length).fill(0)
    );

    const processEngagementData = (data) => {
      const conversationByDay =
        data.conversationStatistics?.averageConversationLengthByDate || [];
      const dailyConversation = Array(dateRange.length).fill(0);

      conversationByDay.forEach((record) => {
        const recordDate = parseLocalDate(record.date);
        const normalizedRecordDate = new Date(recordDate.setHours(0, 0, 0, 0));

        dateRange.forEach((date, index) => {
          const normalizedDate = new Date(date.getTime()).setHours(0, 0, 0, 0);
          if (normalizedRecordDate.getTime() === normalizedDate) {
            const engagementTimeInMinutes = record.averageConversationLength;
            dailyConversation[index] += engagementTimeInMinutes;
          }
        });
      });
      setWeeklyData(dailyConversation);
    };

    useEffect(() => {
      if (!globalStartDate || !globalEndDate || !globalEngagementData) {
        setWeeklyData(Array(dateRange.length).fill(0));
        setChatTime("0 min");
        return;
      }

      processEngagementData(globalEngagementData);

      // Backend now returns a simple string value for the selected date range
      const totalSessionDurationForRange = globalEngagementData.totalSessionDurationForRange;

      if (totalSessionDurationForRange) {
        setChatTime(totalSessionDurationForRange);
      } else {
        setChatTime("0 min");
      }
    }, [globalEngagementData, globalStartDate, globalEndDate]);

    const handleAverageConversationExport = async (format, data, filename, chartCanvas) => {
      const exportData = {
        labels: dateRange.map((date) =>
          date.toLocaleDateString("en-US", {
            weekday: "short",
            month: "short",
            day: "numeric",
          })
        ),
        values: weeklyData
      };

      const metadata = {
        title: 'Average Questions Count',
        subtitle: `Total Chat Time - ${chatTime}`,
        selectedDataset: selectedDatasetName || 'All Datasets',
        selectedGroup: getGroupName(),
        selectedUsers: getSelectedUsersText(),
        dateRange: `${formatDate(globalStartDate)} - ${formatDate(globalEndDate)}`,
        labelHeader: 'Day',
        valueHeader: 'Average Question Number'
      };

      await handleExport(format, exportData, `Average_Questions_Count_${formatDate(globalStartDate)}_${formatDate(globalEndDate)}`, metadata, chartCanvas);
    };

    const chartData = {
      labels: dateRange.map((date) =>
        date.toLocaleDateString("en-US", {
          weekday: "short",
          month: "short",
          day: "numeric",
        })
      ),
      datasets: [
        {
          data: weeklyData,
          borderColor: "#0053ED",
          backgroundColor: "#0053ED",
          borderWidth: 1,
          barPercentage: 0.3,
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            stepSize: 1,
            callback: function (value) {
              return value;
            },
          },
          grid: {
            display: false,
          },
          title: {
            display: true,
            text: "Average Question Number",
            font: {
              size: 14,
              weight: "bold",
            },
          },
        },
        x: {
          title: {
            display: true,
            text: "Day",
            font: {
              size: 14,
              weight: "bold",
            },
          },
          grid: {
            display: false,
          },
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        datalabels: {
          display: false,
          anchor: "hide",
          align: "top",
          color: "white",
          font: {
            weight: "bold",
          },
        },
      },
    };

    return (
      <Grid
        className="card_dashboards"
        style={{
          minHeight: "430px",
          display: "flex",
          justifyContent: "center",
        }}
      >
        {globalEngagementLoading ? (
          <DashLoader />
        ) : (
          <>
            <Grid.Row>
              <Grid.Column className="card-header-title">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                  <div>
                    <Popup
                      content="The average number of questions on all conversation per day"
                      inverted
                      position="top left"
                      style={{ borderRadius: "15px" }}
                      trigger={
                        <label className="chart-title">
                          Average Questions Count
                        </label>
                      }
                    />
                    <h4 className="card-subtitles">Total Chat Time - {chatTime}</h4>
                  </div>
                  <DownloadIcon
                    onExport={handleAverageConversationExport}
                    filename={`Average_Questions_Count_${formatDate(globalStartDate)}_${formatDate(globalEndDate)}`}
                    chartRef={chartRef}
                  />
                </div>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={16} className="chart-containers">
                <Bar ref={chartRef} data={chartData} options={options} />
              </Grid.Column>
            </Grid.Row>
          </>
        )}
      </Grid>
    );
  };

  const renderConversationLengthDistribution = () => {
    const chartRef = useRef(null);
    const [distributionData, setDistributionData] = useState({
      short: 0,
      medium: 0,
      long: 0,
    });

    const processEngagementData = (data) => {
      const conversationLengthDistribution =
        data.conversationLengthDistribution || {};

      setDistributionData({
        short: parseFloat(conversationLengthDistribution.short || 0).toFixed(2),
        medium: parseFloat(conversationLengthDistribution.medium || 0).toFixed(2),
        long: parseFloat(conversationLengthDistribution.long || 0).toFixed(2),
      });
    };

    useEffect(() => {
      if (!globalStartDate || !globalEndDate || !globalEngagementData) {
        setDistributionData({ short: 0, medium: 0, long: 0 });
        return;
      }

      const distArr =
        globalEngagementData.conversationStatistics?.conversationLengthDistribution || [];
      const matchedData = findMatchingWeekData(
        distArr,
        globalStartDate,
        globalEndDate
      );
      if (matchedData) {
        processEngagementData(matchedData);
      } else {
        processEngagementData({});
      }
    }, [globalEngagementData, globalStartDate, globalEndDate]);

    const handleConversationDistributionExport = async (format, data, filename, chartCanvas) => {
      const exportData = {
        labels: ["Short (<=3)", "Medium (<=10)", "Long (>10)"],
        values: [
          distributionData.short,
          distributionData.medium,
          distributionData.long,
        ]
      };

      const metadata = {
        title: 'Distribution of Conversation Lengths',
        subtitle: 'Percentage of conversations by length',
        selectedDataset: selectedDatasetName || 'All Datasets',
        selectedGroup: getGroupName(),
        selectedUsers: getSelectedUsersText(),
        dateRange: `${formatDate(globalStartDate)} - ${formatDate(globalEndDate)}`,
        labelHeader: 'Conversation Length',
        valueHeader: 'Percentage (%)'
      };

      await handleExport(format, exportData, `Conversation_Length_Distribution_${formatDate(globalStartDate)}_${formatDate(globalEndDate)}`, metadata, chartCanvas);
    };

    const chartData = {
      labels: ["Short (<=3)", "Medium (<=10)", "Long (>10)"],
      datasets: [
        {
          label: "Percentage of Conversations",
          data: [
            distributionData.short,
            distributionData.medium,
            distributionData.long,
          ],
          backgroundColor: ["#FF6384", "#36A2EB", "#FFCE56"],
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          title: {
            display: true,
            text: "Count of Questions in Chat",
            font: {
              size: 14,
              weight: "bold",
            },
          },
        },
        y: {
          title: {
            display: true,
            text: "No. of Percentage ",
            font: {
              size: 14,
              weight: "bold",
            },
          },
          ticks: {
            beginAtZero: true,
            callback: function (value) {
              return value + "%";
            },
          },
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: function (context) {
              const value = context.raw;
              return `${value}%`;
            },
          },
        },
      },
    };

    return (
      <Grid
        className="card_dashboards"
        style={{
          minHeight: "430px",
          display: "flex",
          justifyContent: "center",
        }}
      >
        {globalEngagementLoading ? (
          <DashLoader />
        ) : (
          <>
            <Grid.Row>
              <Grid.Column className="card-header-title">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                  <div>
                    <Popup
                      content="The percentage of chats that has short, medium, and long questions for the week selected"
                      inverted
                      position="top left"
                      style={{ borderRadius: "15px" }}
                      trigger={
                        <label className="chart-title">
                          Distribution of Conversation Lengths
                        </label>
                      }
                    />
                  </div>
                  <DownloadIcon
                    onExport={handleConversationDistributionExport}
                    filename={`Conversation_Length_Distribution_${formatDate(globalStartDate)}_${formatDate(globalEndDate)}`}
                    chartRef={chartRef}
                  />
                </div>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={16} className="chart-containers">
                <Bar ref={chartRef} data={chartData} options={options} />
              </Grid.Column>
            </Grid.Row>
          </>
        )}
      </Grid>
    );
  };

  // Longest Conversations
  const renderLongestConversations = () => {
    const chartRef = useRef(null);
    const [longestConversations, setLongestConversations] = useState([]);

    useEffect(() => {
      if (!globalStartDate || !globalEndDate || !globalEngagementData) {
        setLongestConversations([]);
        return;
      }

      const arr = globalEngagementData.longestConversations || [];
      const matchedData = findMatchingWeekData(
        arr,
        globalStartDate,
        globalEndDate
      );
      setLongestConversations(matchedData?.longestConversation || []);
    }, [globalEngagementData, globalStartDate, globalEndDate]);

    const handleLongestConversationsExport = async (format, data, filename, chartCanvas) => {
      const exportData = {
        labels: longestConversations.map((conversation) =>
          truncateText(
            conversation.userQuestion[0][0] || `ChatId${conversation.chatId}`,
            20
          )
        ),
        values: longestConversations.map((data) => data.numQuestions)
      };

      const metadata = {
        title: 'Longest Conversations',
        subtitle: 'Top 5 chats with longest duration',
        selectedDataset: selectedDatasetName || 'All Datasets',
        selectedGroup: getGroupName(),
        selectedUsers: getSelectedUsersText(),
        dateRange: `${formatDate(globalStartDate)} - ${formatDate(globalEndDate)}`,
        labelHeader: 'Chat Title',
        valueHeader: 'Number of Questions'
      };

      await handleExport(format, exportData, `Longest_Conversations_${formatDate(globalStartDate)}_${formatDate(globalEndDate)}`, metadata, chartCanvas);
    };

    const truncateText = (text, maxLength) => {
      if (text.length > maxLength) {
        return text.substring(0, maxLength) + "...";
      }
      return text;
    };

    const chartData = {
      labels: longestConversations.map((conversation) =>
        truncateText(
          conversation.userQuestion[0][0] || `ChatId${conversation.chatId}`,
          6
        )
      ),
      datasets: [
        {
          data: longestConversations.map((data) => data.numQuestions),
          borderColor: "#0053ED",
          backgroundColor: "#0053ED",
          borderWidth: 1,
          barPercentage: 0.3,
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            stepSize: 1,
            callback: function (value) {
              return value;
            },
          },
          grid: {
            display: false,
          },
          title: {
            display: true,
            text: "Counts of Questions in Chat",
            font: {
              size: 14,
              weight: "bold",
            },
            padding: {
              top: 10,
              bottom: 10,
            },
            wrap: true,
          },
        },
        x: {
          title: {
            display: true,
            text: "Top 5 Longest Chat",
            font: {
              size: 14,
              weight: "bold",
            },
          },
          grid: {
            display: false,
          },
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            title: function (context) {
              const index = context[0].dataIndex;
              const questions =
                longestConversations[index]?.userQuestion[0]
                  .slice(0, 1)
                  .join(", ") || "";

              // Split long text into multiple lines (max 50 characters per line)
              const maxCharsPerLine = 50;
              const lines = [];
              const words = questions.split(' ');
              let currentLine = 'Chat Title: ';

              words.forEach((word, i) => {
                if ((currentLine + word).length > maxCharsPerLine && currentLine !== 'Chat Title: ') {
                  lines.push(currentLine.trim());
                  currentLine = word + ' ';
                } else {
                  currentLine += word + ' ';
                }
              });

              if (currentLine.trim()) {
                lines.push(currentLine.trim());
              }

              return lines;
            },
            label: function (context) {
              const index = context.dataIndex;
              const numQuestions =
                longestConversations[index]?.numQuestions || 0;
              return `Questions: ${numQuestions}`;
            },
          },
        },
        datalabels: {
          anchor: "end",
          align: "end",
          color: "white",
          font: {
            weight: "bold",
          },
        },
      },
    };

    return (
      <Grid
        className="card_dashboards"
        style={{
          minHeight: "430px",
          display: "flex",
          justifyContent: "center",
        }}
      >
        {globalEngagementLoading ? (
          <DashLoader />
        ) : (
          <>
            <Grid.Row>
              <Grid.Column className="card-header-title">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                  <div>
                    <Popup
                      content="The Top 5 chats having longest duration for selected week"
                      inverted
                      position="top left"
                      style={{ borderRadius: "15px" }}
                      trigger={
                        <label className="chart-title">Longest Conversations</label>
                      }
                    />
                  </div>
                  <DownloadIcon
                    onExport={handleLongestConversationsExport}
                    filename={`Longest_Conversations_${formatDate(globalStartDate)}_${formatDate(globalEndDate)}`}
                    chartRef={chartRef}
                  />
                </div>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={16} className="chart-containers">
                <Bar ref={chartRef} data={chartData} options={options} />
              </Grid.Column>
            </Grid.Row>
          </>
        )}
      </Grid>
    );
  };

  // Frequent Issues/Topics
  const renderFrequentIssues = () => {
    const chartRef = useRef(null);
    const [frequentIssues, setFrequentIssues] = useState([]);

    useEffect(() => {
      if (!globalStartDate || !globalEndDate || !globalEngagementData) {
        setFrequentIssues([]);
        return;
      }

      const arr = globalEngagementData.frequentlyAskedQuestions || [];
      const matchedData = findMatchingWeekData(
        arr,
        globalStartDate,
        globalEndDate
      );
      setFrequentIssues(matchedData?.questions || []);
    }, [globalEngagementData, globalStartDate, globalEndDate]);

    const handleFrequentIssuesExport = async (format, data, filename, chartCanvas) => {
      const exportData = {
        labels: frequentIssues.map((_, index) => `Q${index + 1}`),
        values: frequentIssues.map((issue) => issue.count)
      };

      // Add detailed questions to export
      const detailedData = frequentIssues.map((issue, index) => ({
        Question_ID: `Q${index + 1}`,
        Question: issue.question,
        Count: issue.count
      }));

      const metadata = {
        title: 'Frequent Issues/Questions',
        subtitle: 'Top 5 most frequently asked questions',
        selectedDataset: selectedDatasetName || 'All Datasets',
        selectedGroup: getGroupName(),
        selectedUsers: getSelectedUsersText(),
        dateRange: `${formatDate(globalStartDate)} - ${formatDate(globalEndDate)}`,
        labelHeader: 'Question ID',
        valueHeader: 'Count'
      };

      await handleExport(format, detailedData.length > 0 ? detailedData : exportData, `Frequent_Issues_${formatDate(globalStartDate)}_${formatDate(globalEndDate)}`, metadata, chartCanvas);
    };

    const chartData = {
      labels: frequentIssues.map((_, index) => `Q${index + 1}`),
      datasets: [
        {
          data: frequentIssues.map((issue) => issue.count),
          borderColor: "#0053ED",
          backgroundColor: "#0053ED",
          borderWidth: 1,
          barPercentage: 0.3,
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            stepSize: 1,
            callback: function (value) {
              return value;
            },
          },
          grid: {
            display: false,
          },
          title: {
            display: true,
            text: "No. of times a question asked",
            font: {
              size: 14,
              weight: "bold",
            },
          },
        },
        x: {
          title: {
            display: true,
            text: "Top 5 Frequently asked Questions",
            font: {
              size: 14,
              weight: "bold",
            },
          },
          grid: {
            display: false,
          },
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            title: function (context) {
              const index = context[0].dataIndex;
              return `Q${index + 1}`;
            },
            label: function (context) {
              const index = context.dataIndex;
              const count = frequentIssues[index]?.count || 0;
              return `Count: ${count}`;
            },
            afterLabel: function (context) {
              const index = context.dataIndex;
              const question = frequentIssues[index]?.question || "";

              // Split long text into multiple lines (max 50 characters per line)
              const maxCharsPerLine = 50;
              const lines = [];
              const words = question.split(' ');
              let currentLine = 'Question: ';

              words.forEach((word, i) => {
                if ((currentLine + word).length > maxCharsPerLine && currentLine !== 'Question: ') {
                  lines.push(currentLine.trim());
                  currentLine = word + ' ';
                } else {
                  currentLine += word + ' ';
                }
              });

              if (currentLine.trim()) {
                lines.push(currentLine.trim());
              }

              return lines;
            },
          },
        },
        datalabels: {
          anchor: "end",
          align: "end",
          color: "white",
          font: {
            weight: "bold",
          },
        },
      },
    };

    return (
      <Grid
        className="card_dashboards"
        style={{
          minHeight: "430px",
          display: "flex",
          justifyContent: "center",
        }}
      >
        {globalEngagementLoading ? (
          <DashLoader />
        ) : (
          <>
            <Grid.Row>
              <Grid.Column className="card-header-title">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                  <div>
                    <Popup
                      content="Display the Top 5 most frequently asked questions"
                      inverted
                      position="top left"
                      style={{ borderRadius: "15px" }}
                      trigger={
                        <label className="chart-title">
                          Frequent Issues/Questions
                        </label>
                      }
                    />
                  </div>
                  <DownloadIcon
                    onExport={handleFrequentIssuesExport}
                    filename={`Frequent_Issues_${formatDate(globalStartDate)}_${formatDate(globalEndDate)}`}
                    chartRef={chartRef}
                  />
                </div>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={16} className="chart-containers">
                <Bar ref={chartRef} data={chartData} options={options} />
              </Grid.Column>
            </Grid.Row>
          </>
        )}
      </Grid>
    );
  };

  return (
    <Grid className="dashboards" stackable>
      <Grid.Row columns={2}>
        <Grid.Column className="dashboard-cards">
          <div className="chart-container-engagement">
            {renderAverageConversationLength()}
          </div>
        </Grid.Column>
        <Grid.Column className="dashboard-cards">
          <div className="chart-container-engagement">
            {renderConversationLengthDistribution()}
          </div>
        </Grid.Column>
      </Grid.Row>
      <Grid.Row columns={2}>
        <Grid.Column className="dashboard-cards">
          <div className="chart-container-engagement">
            {renderLongestConversations()}
          </div>
        </Grid.Column>
        <Grid.Column className="dashboard-cards">
          <div className="chart-container-engagement">
            {renderFrequentIssues()}
          </div>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

export default ConversationEngagement;
